import React, {useEffect} from 'react';
import {Grid} from '@mui/material';
import {useField} from 'formik';
import {get} from 'lodash-es';
import {ActionType, AutoField} from '../../../components/AutoForm/AutoFieldNew';
import usePlanNamesProviders, {
  PlanProviderSelectOption,
} from '../../../hooks/usePlanNamesProviders';
import {recommendFieldsType} from '../CreateRecommend/recommendFields';
import type {newPlanFields} from './setUpNewFields';
import {setUpNewPlanProviderField as recommendPlanProviderField} from './setUpNewFields';
import type {modelPortfolioField} from '../RecommendModify/modifyFieldTypes';
import {withdrawalAmountField as recommendAmountField} from '../RecommendsWithdrawal/withdrawalFields';
import {OVRecommendActionEnum} from '../../../schema-types/globalTypes';
import {MandatesWrapper} from '../../../pages/MandatesPage/MandatesModal';
import {useSPPProviders} from '../CreateRecommend/useSPPProviders';
import {PlanFeesField} from '../CreateRecommend/PlanFees';
import useComboFrequency from '../../../hooks/useComboFrequency';
import {ShowIfDebugWrapper} from '../../../contexts/DebugProvider';
import {useAutoField, useAutoForm} from '../../../components/AutoForm/useAutoForm';
import {UPDATES} from '../../../shared/UPDATES';
import {shouldShowEmployerContribution} from '../RecommendsContribute/ContributeHelpers';
import {useIsMinor} from '../hooks/createRecommendHooks';
import validateUUID from '../../../utils/validateUUID';
import {sanitiseProductType} from '../../../utils/sanitiseProductType';
import {useAccountContext} from '../../../contexts/AccountContext';

export interface SubPlanProps {
  formikKeyPrefix: string;
  context: 'setUpNew' | 'transfer' | 'contribute';
  disableAmount?: boolean;
}

export const NewSubPlanDetails = ({
  formikKeyPrefix,
  disableAmount,
  context,
  showMandateSelector,
  planProviderOptionsReduced,
}: SubPlanProps & {
  showMandateSelector?: boolean;
  planProviderOptionsReduced: PlanProviderSelectOption[];
}) => {
  const {values} = useAutoForm<recommendFieldsType>();
  const isMinor = useIsMinor();
  const comboFrequency =
    context === 'contribute' ? values.contribute?.comboFrequency : undefined; // transfer and setUpNew are always single
  const {hasRecurring, hasSingle} = useComboFrequency(comboFrequency);
  const [mandateField, mandateMeta, mandateHelpers] = useAutoField<
    modelPortfolioField | undefined
  >(`${formikKeyPrefix}.model`);
  const [parentProductIdField] = useField<newPlanFields['planType']>(
    `${context}.newPlan.planType`
  );

  const {disableProviderField} = useSPPProviders(
    `${formikKeyPrefix}.planProvider`,
    null,
    planProviderOptionsReduced,
    false,
    true
  );

  const {isSaltusProvider, subproductProviders, queryGetProductProviders} =
    usePlanNamesProviders();

  const {accountId} = useAccountContext();
  useEffect(() => {
    // this MUST refresh when mainPlanType changes,
    // otherwise we'll have GIAs showing when ISA is selected
    if (values.mainPlanType) {
      queryGetProductProviders({
        variables: {
          accountId: validateUUID(accountId),
          productType: sanitiseProductType(values.mainPlanType),
          productBasis: 'accumulation',
        },
      });
    }
  }, [values.mainPlanType]);

  const planProvider: string | null = get(
    values,
    `${formikKeyPrefix}.planProvider`,
    null
  );
  return (
    <Grid pt={2} container direction="row" spacing={2}>
      <Grid item xs={6}>
        <AutoField
          field={{
            disabled: !subproductProviders?.length || disableProviderField,
            ...recommendPlanProviderField,
            label: subproductProviders?.length
              ? `Select subplan provider`
              : 'Loading plans',
            id: `${formikKeyPrefix}.planProvider`,
            type: ActionType.selectAutocomplete,
            options: subproductProviders || [],
          }}
          onBlur={() => {
            mandateHelpers.setValue(UPDATES._527, undefined, true);
          }}
        />
      </Grid>
      <Grid item xs={6}>
        <MandatesWrapper
          isChild
          showCurrentMandate={false}
          planProviderOptions={subproductProviders}
          formikKeyPrefix={formikKeyPrefix}
        />
      </Grid>
      <Grid item xs={12}>
        <ShowIfDebugWrapper>
          FormikKeyPrefix: {formikKeyPrefix}
          <br />
          parentProductIdField: {parentProductIdField.value}
        </ShowIfDebugWrapper>
        {/* PlanType: {planType} */}

        <PlanFeesField
          inModify={false}
          hasSubplan={false}
          requiresMandate={showMandateSelector || false}
          mandateId={
            mandateField.value && typeof mandateField.value === 'object'
              ? mandateField.value?.id
              : undefined
          }
          providerId={planProvider}
          productId={parentProductIdField.value} // just for the time being
          formikKeyPrefixWithoutFee={formikKeyPrefix}
          parentProductId={parentProductIdField.value}
          isSaltus={isSaltusProvider(planProvider ?? '')}
        />
      </Grid>
      {/* spacer */}
      <Grid item xs={12} />
      {(values.planAction === OVRecommendActionEnum.TRANSFER ||
        values.planAction === OVRecommendActionEnum.CONTRIBUTE) &&
        hasSingle && (
          <Grid item xs={6}>
            <AutoField
              field={{
                ...recommendAmountField,
                disabled: disableAmount,
                allowZero: true,
                canBeEmpty: true,
                id: `${formikKeyPrefix}.singleClientAmount`,
                label: `Personal Single Amount${
                  values.mainPlanType === 'pension' ? ' (Gross)' : ''
                }`,
              }}
            />
          </Grid>
        )}
      {values.planAction === OVRecommendActionEnum.CONTRIBUTE && hasRecurring && (
        <Grid item xs={6}>
          <AutoField
            field={{
              ...recommendAmountField,
              allowZero: true,
              canBeEmpty: true,
              id: `${formikKeyPrefix}.recurringClientAmount`,
              label: `Personal Recurring amount${
                values.mainPlanType === 'pension' ? ' (Gross)' : ''
              }`,
            }}
          />
        </Grid>
      )}
      {values.planAction === OVRecommendActionEnum.CONTRIBUTE &&
        shouldShowEmployerContribution({
          isMinor,
          mainPlanType: values.mainPlanType,
        }) &&
        hasSingle && (
          <Grid item xs={6}>
            <AutoField
              field={{
                ...recommendAmountField,
                allowZero: true,
                canBeEmpty: true,
                id: `${formikKeyPrefix}.singleEmployerAmount`,
                label: 'Employer Single Amount (Gross)',
              }}
            />
          </Grid>
        )}
      {values.planAction === OVRecommendActionEnum.CONTRIBUTE &&
        shouldShowEmployerContribution({
          isMinor,
          mainPlanType: values.mainPlanType,
        }) &&
        hasRecurring && (
          <Grid item xs={6}>
            <AutoField
              field={{
                ...recommendAmountField,
                allowZero: true,
                canBeEmpty: true,
                id: `${formikKeyPrefix}.recurringEmployerAmount`,
                label: 'Employer Recurring amount (Gross)',
              }}
            />
          </Grid>
        )}
    </Grid>
  );
};
